import { apiRequest } from '@/lib/api-client';

export interface Product {
  id: string;
  name: string;
  description: string;
  category: string;
  brand: string;
  price: { amount: number; currency: string };
  stock: number;
  images: string[];
  url: string | null;
  sku: string | null;
  externalId: string | null;
  createdAt: string;
}

export interface PaginatedProducts {
  items: Product[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}

export interface IngestionSummary {
  created: number;
  updated: number;
  failed: number;
  errors: Array<{ index: number; message: string }>;
}

export interface ProductInput {
  name: string;
  description?: string;
  category?: string;
  brand?: string;
  price?: { amount: number; currency: string };
  stock?: number;
  url?: string;
  sku?: string;
}

export interface ProductSource {
  id: string;
  name: string;
  url: string;
  hasToken: boolean;
  enabled: boolean;
  lastSyncedAt: string | null;
  lastStatus: 'never' | 'ok' | 'error';
  lastError: string | null;
  lastCount: number;
}

export interface ProductSourceInput {
  name: string;
  url: string;
  bearerToken?: string;
}

const base = (orgId: string, projectId: string) =>
  `/api/v1/organizations/${orgId}/projects/${projectId}/products`;

export const catalogueApi = {
  list: (orgId: string, projectId: string, page: number, pageSize: number) =>
    apiRequest<PaginatedProducts>(base(orgId, projectId), { params: { page, pageSize } }),

  add: (orgId: string, projectId: string, product: ProductInput) =>
    apiRequest<IngestionSummary>(base(orgId, projectId), { method: 'POST', body: product }),

  importCsv: (orgId: string, projectId: string, csvContent: string) =>
    apiRequest<IngestionSummary>(`${base(orgId, projectId)}/import/csv`, {
      method: 'POST',
      body: { csvContent },
    }),

  remove: (orgId: string, projectId: string, productId: string) =>
    apiRequest<{ deleted: boolean }>(`${base(orgId, projectId)}/${productId}`, {
      method: 'DELETE',
    }),

  brands: (orgId: string, projectId: string) =>
    apiRequest<string[]>(`${base(orgId, projectId)}/brands`),

  bulkDelete: (orgId: string, projectId: string, body: { productIds?: string[]; all?: boolean }) =>
    apiRequest<{ deleted: number }>(`${base(orgId, projectId)}/bulk-delete`, {
      method: 'POST',
      body,
    }),

  listSources: (orgId: string, projectId: string) =>
    apiRequest<ProductSource[]>(`${base(orgId, projectId)}/sources`),
  createSource: (orgId: string, projectId: string, body: ProductSourceInput) =>
    apiRequest<ProductSource>(`${base(orgId, projectId)}/sources`, { method: 'POST', body }),
  deleteSource: (orgId: string, projectId: string, sourceId: string) =>
    apiRequest<{ deleted: boolean }>(`${base(orgId, projectId)}/sources/${sourceId}`, {
      method: 'DELETE',
    }),
  syncSource: (orgId: string, projectId: string, sourceId: string) =>
    apiRequest<{ status: string; summary?: IngestionSummary }>(
      `${base(orgId, projectId)}/sources/${sourceId}/sync`,
      { method: 'POST' },
    ),
  scheduleSync: (orgId: string, projectId: string, cadenceHours = 24) =>
    apiRequest<{ scheduleId: string }>(`${base(orgId, projectId)}/sources/schedule`, {
      method: 'POST',
      body: { cadenceHours },
    }),
};
